import DoctorSelection from "./DoctorSelection";
/* Dedicated kiosk namespace is bundled in kioskTranslations.ts, outside the shared locale scanner. */
/* eslint-disable i18next-no-undefined-translation-keys/no-undefined-translation-keys */
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import query, { callApi } from "@/Utils/request/query";
import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CalendarPlus,
  CheckCircle2,
  RotateCcw,
  UserPlus,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { checkInApi, kioskApi, KioskOption } from "./api";
import i18n from "./kioskTranslations";

type Mode = "checkin" | "walkin" | "new";
type Step =
  | "home"
  | "phone"
  | "patients"
  | "appointments"
  | "details"
  | "doctors"
  | "slots"
  | "confirm"
  | "receipt";
const modes = [
  { mode: "checkin", icon: CalendarCheck },
  { mode: "walkin", icon: Users },
  { mode: "new", icon: UserPlus },
] as const;
const languages = [
  { id: "en", label: "English" },
  { id: "ml", label: "മലയാളം" },
];
const genders = ["male", "female", "non_binary", "transgender"];
const listSteps: Step[] = ["patients", "appointments", "slots"];
export default function PublicCheckIn({ checkInKey }: { checkInKey: string }) {
  const [language, setLanguage] = useState("en");
  const t = i18n.getFixedT(language, "kiosk");
  const {
    data: info,
    isLoading,
    error: infoError,
  } = useQuery({
    queryKey: ["public-check-in", checkInKey],
    queryFn: query(checkInApi.publicInfo, {
      pathParams: { key: checkInKey },
      silent: true,
    }),
    retry: false,
  });
  const [step, setStep] = useState<Step>("home");
  const [mode, setMode] = useState<Mode>("checkin");
  const [lastBookingDate] = useState(() =>
    new Date(Date.now() + 90 * 86400000).toLocaleDateString("en-CA"),
  );
  const [bookingOnly, setBookingOnly] = useState(false);
  const [bookedReceipt, setBookedReceipt] = useState(false);
  const [visitDate, setVisitDate] = useState(
    new Date().toLocaleDateString("en-CA"),
  );
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  const reserveOnly =
    bookingOnly ||
    (mode === "new" && visitDate !== new Date().toLocaleDateString("en-CA"));
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [patient, setPatient] = useState<KioskOption>();
  const [doctor, setDoctor] = useState<KioskOption>();
  const [slot, setSlot] = useState<KioskOption>();
  const [booking, setBooking] = useState<KioskOption>();
  const [options, setOptions] = useState<KioskOption[]>([]);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  const [receipt, setReceipt] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);
  const requestId = useRef(crypto.randomUUID());
  const reset = useCallback(() => {
    generation.current++;
    setStep("home");
    setBookingOnly(false);
    setBookedReceipt(false);
    setVisitDate(new Date().toLocaleDateString("en-CA"));
    setPhone("");
    setToken("");
    setName("");
    setDob("");
    setGender("");
    setPatient(undefined);
    setDoctor(undefined);
    setSlot(undefined);
    setBooking(undefined);
    setOptions([]);
    setReceipt("");
    setBusy(false);
    setFailed(false);
    requestId.current = crypto.randomUUID();
  }, []);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const activity = () => {
      clearTimeout(timer);
      timer = setTimeout(reset, step === "receipt" ? 30000 : 120000);
    };
    activity();
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
    };
  }, [step, reset]);
  const run = async (fn: (current: number) => Promise<void>) => {
    if (busy) return;
    const current = generation.current;
    setBusy(true);
    setFailed(false);
    try {
      await fn(current);
    } catch {
      if (current === generation.current) setFailed(true);
    } finally {
      if (current === generation.current) setBusy(false);
    }
  };
  const load = async (
    kind: "patients" | "appointments" | "doctors" | "slots",
    access = token,
    start = 0,
    doctorId = doctor?.id,
    current = generation.current,
    day = visitDate,
  ) => {
    if (kind === "doctors") {
      setStep("doctors");
      return;
    }
    const result = await callApi(kioskApi.list, {
      pathParams: { key: checkInKey },
      headers: { Authorization: `Bearer ${access}` },
      queryParams: {
        kind,
        offset: start,
        doctor: doctorId,
        day: bookingOnly || mode === "new" ? day : undefined,
      },
      silent: true,
    });
    if (current !== generation.current) return;
    setOptions(result.results);
    setCount(result.count);
    setOffset(start);
    setStep(kind);
  };
  const enterPhone = () =>
    run(async (current) => {
      if (!isValidPhoneNumber(phone)) throw new Error("Invalid phone");
      const result = await callApi(kioskApi.session, {
        pathParams: { key: checkInKey },
        body: { phone_number: phone },
        silent: true,
      });
      if (current !== generation.current) return;
      setToken(result.access);
      if (mode === "new") setStep("details");
      else
        await load(
          mode === "checkin" ? "appointments" : "patients",
          result.access,
          0,
          undefined,
          current,
        );
    });
  const submit = () =>
    run(async (current) => {
      const result = await callApi(kioskApi.submit, {
        pathParams: { key: checkInKey },
        headers: { Authorization: `Bearer ${token}` },
        body: {
          mode,
          booking_only: reserveOnly,
          request_id: requestId.current,
          booking: booking?.id,
          patient: patient?.id,
          slot: slot?.id,
          ...(mode === "new" ? { name, date_of_birth: dob, gender } : {}),
        },
        silent: true,
      });
      if (current !== generation.current) return;
      const wasBooking = reserveOnly;
      reset();
      setBookedReceipt(wasBooking);
      setReceipt(result.receipt.slice(0, 8).toUpperCase());
      setStep("receipt");
    });
  const choose = (option: KioskOption) =>
    run(async (current) => {
      if (step === "appointments") {
        setBooking(option);
        setStep("confirm");
      } else if (step === "patients") {
        setPatient(option);
        await load("doctors", token, 0, undefined, current);
      } else if (step === "doctors") {
        setDoctor(option);
        await load("slots", token, 0, option.id, current);
      } else {
        setSlot(option);
        setStep("confirm");
      }
    });
  const back = () => {
    setFailed(false);
    if (step === "phone" || step === "receipt") reset();
    else if (
      step === "details" ||
      step === "patients" ||
      step === "appointments"
    )
      reset();
    else if (step === "doctors") {
      if (mode === "new") setStep("details");
      else void run((c) => load("patients", token, 0, undefined, c));
    } else if (step === "slots")
      void run((c) => load("doctors", token, 0, undefined, c));
    else if (step === "confirm")
      void run((c) =>
        load(
          mode === "checkin" ? "appointments" : "slots",
          token,
          0,
          doctor?.id,
          c,
        ),
      );
  };
  const formatTime = (value?: string) =>
    value
      ? new Date(value).toLocaleTimeString(
          language === "ml" ? "ml-IN" : "en-IN",
          { hour: "numeric", minute: "2-digit", hour12: true },
        )
      : "";
  const title =
    step === "receipt"
      ? t(bookedReceipt ? "appointmentBooked" : "received")
      : step === "home"
        ? t("welcome")
        : step === "phone"
          ? t("phoneTitle")
          : t(
              (bookingOnly || mode === "new") && step === "slots"
                ? "bookingSlots"
                : step,
            );
  const progress =
    mode === "checkin"
      ? ["progressPatient", "progressAppointment", "confirm"]
      : ["progressPatient", "progressDoctor", "progressTime", "confirm"];
  const progressIndex =
    step === "confirm"
      ? progress.length - 1
      : step === "slots"
        ? 2
        : step === "doctors" || step === "appointments"
          ? 1
          : 0;
  return (
    <main
      lang={language}
      className="min-h-svh bg-slate-50 px-4 pt-4 pb-32 sm:px-6"
    >
      <div className="mx-auto max-w-5xl">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <img
            src={careConfig.mainLogo?.dark}
            alt={t("logo")}
            className="h-10 sm:h-12 max-w-full w-auto object-contain object-left"
          />
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            {step !== "home" && step !== "receipt" && (
              <Button
                variant="ghost"
                onClick={reset}
                disabled={busy}
                className="min-h-11 text-slate-600 hover:bg-slate-200/60"
              >
                <RotateCcw className="size-4" aria-hidden="true" />
                {t("home")}
              </Button>
            )}
            <div
              className="flex flex-wrap rounded-xl border bg-white p-1 gap-1"
              aria-label={t("language")}
            >
              {languages.map((l) => (
                <Button
                  key={l.id}
                  className="min-h-11 px-3 sm:px-5"
                  variant={language === l.id ? "secondary" : "ghost"}
                  aria-pressed={language === l.id}
                  onClick={() => setLanguage(l.id)}
                >
                  {l.label}
                </Button>
              ))}
            </div>
          </div>
        </header>
        {isLoading ? (
          <p role="status">{t("loading")}</p>
        ) : infoError ? (
          <p role="alert">{t("unavailable")}</p>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-sm text-slate-500 mb-1">{info?.facility}</p>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                {title}
              </h1>
              {step === "home" && (
                <p className="mt-3 text-lg text-slate-600">{t("subtitle")}</p>
              )}
            </div>
            {step !== "home" && step !== "receipt" && (
              <ol
                aria-label={t("progress")}
                className="mb-4 grid grid-flow-col auto-cols-fr gap-2"
              >
                {progress.map((label, index) => (
                  <li
                    key={label}
                    aria-current={index === progressIndex ? "step" : undefined}
                    className={`border-t-2 pt-2 text-xs sm:text-sm ${index <= progressIndex ? "border-primary-600 text-primary-700 font-medium" : "border-slate-200 text-slate-500"}`}
                  >
                    {index + 1}. {t(label)}
                  </li>
                ))}
              </ol>
            )}
            {step === "home" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <button
                  className="rounded-2xl border bg-white p-5 sm:p-6 text-left shadow-sm hover:border-primary-500 focus-visible:outline-2 focus-visible:outline-primary-500 flex md:flex-col items-center md:items-start gap-5 min-h-32 md:min-h-48"
                  onClick={() => {
                    setBookingOnly(true);
                    setMode("walkin");
                    setStep("phone");
                  }}
                >
                  <span className="rounded-2xl bg-primary-50 p-4 text-primary-700">
                    <CalendarPlus className="size-8" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-xl sm:text-2xl font-semibold mb-2">
                      {t("bookAppointment")}
                    </span>
                    <span className="block text-sm sm:text-base text-slate-500">
                      {t("bookHint")}
                    </span>
                  </span>
                  <ArrowRight className="size-5 text-primary-700" />
                </button>
                {modes.map(({ mode: choice, icon: Icon }) => (
                  <button
                    key={choice}
                    className="rounded-2xl border bg-white p-5 sm:p-6 text-left shadow-sm hover:border-primary-500 focus-visible:outline-2 focus-visible:outline-primary-500 flex md:flex-col items-center md:items-start gap-5 min-h-32 md:min-h-48"
                    onClick={() => {
                      setMode(choice);
                      setStep("phone");
                    }}
                  >
                    <span className="rounded-2xl bg-primary-50 p-4 text-primary-700">
                      <Icon className="size-8" />
                    </span>
                    <span className="flex-1">
                      <span className="block text-xl sm:text-2xl font-semibold mb-2">
                        {t(choice)}
                      </span>
                      <span className="block text-sm sm:text-base text-slate-500">
                        {t(`${choice}Hint`)}
                      </span>
                    </span>
                    <ArrowRight className="size-5 text-primary-700" />
                  </button>
                ))}
              </div>
            ) : (
              <section
                className={`${step === "doctors" ? "max-w-5xl" : "max-w-2xl"} mx-auto rounded-2xl border bg-white p-4 sm:p-5 space-y-4`}
              >
                {(bookingOnly || mode === "new") &&
                  (step === "patients" ||
                    step === "details" ||
                    step === "slots") && (
                    <div className="space-y-2">
                      <p id="visit-date-label" className="text-sm font-medium">
                        {t("visitDate")}
                      </p>
                      <div role="group" aria-labelledby="visit-date-label">
                        <DatePicker
                          touchFriendly
                          date={new Date(`${visitDate}T00:00:00`)}
                          className="min-h-12 w-full sm:w-full text-base"
                          disablePicker={busy}
                          disabled={(date) => {
                            const value = date.toLocaleDateString("en-CA");
                            return (
                              value < new Date().toLocaleDateString("en-CA") ||
                              value > lastBookingDate
                            );
                          }}
                          onChange={(date) => {
                            if (!date) return;
                            const value = date.toLocaleDateString("en-CA");
                            setVisitDate(value);
                            setSlot(undefined);
                            if (step === "slots")
                              void run((c) =>
                                load("slots", token, 0, doctor?.id, c, value),
                              );
                          }}
                        />
                      </div>
                    </div>
                  )}
                {bookingOnly && step === "patients" && (
                  <Button
                    variant="outline"
                    className="min-h-12"
                    onClick={() => {
                      setMode("new");
                      setStep("details");
                    }}
                  >
                    {t("new")}
                  </Button>
                )}
                {step === "phone" && (
                  <form
                    id="kiosk-phone-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      enterPhone();
                    }}
                    className="space-y-5"
                  >
                    <p className="text-slate-600">{t("phoneHint")}</p>
                    <Label htmlFor="kiosk-phone">{t("phone")}</Label>
                    <PhoneInput
                      id="kiosk-phone"
                      defaultCountry="IN"
                      international
                      autoComplete="off"
                      className="[&_input]:h-14 [&_input]:text-xl [&_button]:min-h-14"
                      value={phone}
                      onChange={(value) => setPhone(value || "")}
                      required
                    />
                  </form>
                )}
                {step === "details" && (
                  <form
                    id="kiosk-details-form"
                    className="space-y-5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run((c) => load("doctors", token, 0, undefined, c));
                    }}
                  >
                    <p>{t("detailsHint")}</p>
                    <div className="space-y-2">
                      <Label htmlFor="kiosk-name">{t("name")}</Label>
                      <Input
                        id="kiosk-name"
                        className="h-14 text-lg"
                        autoComplete="off"
                        maxLength={120}
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="kiosk-dob">{t("dob")}</Label>
                        <DatePicker
                          touchFriendly
                          id="kiosk-dob"
                          className="h-14 min-h-14 w-full sm:w-full"
                          date={dob ? new Date(`${dob}T00:00:00`) : undefined}
                          onChange={(date) =>
                            setDob(date?.toLocaleDateString("en-CA") || "")
                          }
                          disabled={(date) =>
                            date > new Date() ||
                            date <
                              new Date(new Date().getFullYear() - 130, 0, 1)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="kiosk-gender">{t("gender")}</Label>
                        <Select value={gender} onValueChange={setGender}>
                          <SelectTrigger
                            id="kiosk-gender"
                            className="w-full min-h-14 data-[size=default]:h-14 text-base"
                          >
                            <SelectValue placeholder={t("selectGender")} />
                          </SelectTrigger>
                          <SelectContent>
                            {genders.map((g) => (
                              <SelectItem
                                value={g}
                                key={g}
                                className="min-h-12 text-base"
                              >
                                {t(g)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </form>
                )}
                {step === "doctors" && (
                  <DoctorSelection
                    checkInKey={checkInKey}
                    token={token}
                    day={bookingOnly || mode === "new" ? visitDate : undefined}
                    language={language}
                    busy={busy}
                    onSelect={(option) => void choose(option)}
                  />
                )}
                {listSteps.includes(step) && (
                  <div className="space-y-3">
                    {step === "appointments" && (
                      <p className="text-sm text-slate-500">
                        {t("appointmentOnly")}
                      </p>
                    )}
                    {options.length === 0 && (
                      <div className="space-y-3 py-4">
                        <p>{t("noResults")}</p>
                        <p className="text-sm text-slate-500">
                          {t("askReception")}
                        </p>
                        {step === "slots" && (
                          <Button
                            variant="outline"
                            className="min-h-12"
                            onClick={() =>
                              void run((c) =>
                                load("doctors", token, 0, undefined, c),
                              )
                            }
                          >
                            {t("anotherDoctor")}
                          </Button>
                        )}
                      </div>
                    )}
                    <div
                      className={
                        step === "slots"
                          ? "grid grid-cols-2 sm:grid-cols-3 gap-3"
                          : "space-y-3"
                      }
                    >
                      {options.map((o) => (
                        <button
                          disabled={busy}
                          key={o.id}
                          onClick={() => void choose(o)}
                          className="w-full rounded-xl border p-4 min-h-16 flex items-center justify-between text-left gap-3 hover:bg-primary-50 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50"
                        >
                          <span className="min-w-0 break-words">
                            <span className="block font-semibold text-lg">
                              {step === "slots" ? formatTime(o.time) : o.name}
                            </span>
                            {o.doctor && (
                              <span className="block text-slate-500">
                                {o.doctor} · {formatTime(o.time)}
                              </span>
                            )}
                            {o.date_of_birth && (
                              <span className="block text-sm text-slate-500">
                                {t("dob")}: {o.date_of_birth}
                              </span>
                            )}
                          </span>
                          <ArrowRight className="size-5 shrink-0" />
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-between gap-3">
                      <Button
                        variant="outline"
                        className="min-h-12"
                        disabled={!offset || busy}
                        onClick={() =>
                          void run((c) =>
                            load(
                              step as "patients",
                              token,
                              offset - 20,
                              doctor?.id,
                              c,
                            ),
                          )
                        }
                      >
                        {t("previous")}
                      </Button>
                      <Button
                        variant="outline"
                        className="min-h-12"
                        disabled={offset + 20 >= count || busy}
                        onClick={() =>
                          void run((c) =>
                            load(
                              step as "patients",
                              token,
                              offset + 20,
                              doctor?.id,
                              c,
                            ),
                          )
                        }
                      >
                        {t("more")}
                      </Button>
                    </div>
                  </div>
                )}
                {step === "confirm" && (
                  <div className="space-y-6">
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm text-slate-500">
                          {t("selectedPatient")}
                        </dt>
                        <dd className="text-xl font-semibold break-words">
                          {booking?.name || patient?.name || name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-slate-500">
                          {t("selectedDoctor")}
                        </dt>
                        <dd className="text-lg break-words">
                          {booking?.doctor || doctor?.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-slate-500">
                          {t("selectedTime")}
                        </dt>
                        <dd className="text-lg">
                          {(bookingOnly || mode === "new") && `${visitDate} · `}
                          {formatTime(booking?.time || slot?.time)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
                {step === "receipt" && (
                  <div role="status" className="text-center space-y-5">
                    <CheckCircle2 className="size-14 text-primary-600 mx-auto" />
                    <h2 className="text-2xl font-semibold">
                      {t(bookedReceipt ? "appointmentBooked" : "received")}
                    </h2>
                    <p className="text-slate-500">
                      {t(bookedReceipt ? "bookingReference" : "receipt")}
                    </p>
                    <p className="text-3xl tracking-widest font-semibold">
                      {receipt}
                    </p>
                    <p>{t(bookedReceipt ? "bookingReceipt" : "reception")}</p>
                    <Button className="w-full min-h-14 text-lg" onClick={reset}>
                      {t("done")}
                    </Button>
                    <p className="text-sm text-slate-500">{t("reset")}</p>
                  </div>
                )}
                {busy && <p role="status">{t("loading")}</p>}
                {failed && (
                  <p role="alert" className="text-red-700">
                    {t("error")}
                  </p>
                )}
              </section>
            )}
            {step !== "home" && step !== "receipt" && (
              <nav
                aria-label={t("navigation")}
                className="fixed bottom-0 inset-x-0 z-20 border-t bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-sm"
              >
                <div className="mx-auto max-w-5xl flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={back}
                    disabled={busy}
                    className="min-h-12"
                  >
                    <ArrowLeft className="size-4" />
                    {t("back")}
                  </Button>

                  {(step === "phone" || step === "details") && (
                    <Button
                      type="submit"
                      form={
                        step === "phone"
                          ? "kiosk-phone-form"
                          : "kiosk-details-form"
                      }
                      disabled={
                        busy ||
                        (step === "details" &&
                          (!gender || !dob || !name.trim()))
                      }
                      className="ml-auto min-h-12 px-6"
                    >
                      {t("next")}
                      <ArrowRight className="size-4" />
                    </Button>
                  )}
                  {step === "confirm" && (
                    <Button
                      onClick={submit}
                      disabled={busy}
                      className="ml-auto min-h-12 h-auto whitespace-normal text-right max-w-[75%]"
                    >
                      {t(
                        reserveOnly
                          ? "bookAppointment"
                          : mode === "checkin"
                            ? "confirmCheckin"
                            : "confirmWalkin",
                      )}
                    </Button>
                  )}
                </div>
              </nav>
            )}
            <footer className="mt-4 text-center space-y-3 text-sm text-slate-500">
              <p className="font-medium text-slate-700">{t("urgent")}</p>
              {step !== "home" && step !== "receipt" && <p>{t("privacy")}</p>}
            </footer>
          </>
        )}
      </div>
    </main>
  );
}
